import { TimeService } from "@dexteel/mesf-core";
import {
	Alert,
	alpha,
	Box,
	Button,
	InputAdornment,
	Paper,
	Stack,
	TextField,
	Typography,
	type Theme,
	type TextFieldProps,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useId, type ReactNode } from "react";
import {
	type Control,
	Controller,
	useController,
	useWatch,
	type UseFormReturn,
} from "react-hook-form";

import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { InspectionHeatField } from "../../shared/components/InspectionHeatField";
import type { SectionSpec, SectionSpecItem } from "@/shared/models/SectionSpec";
import type { InspectionWithValues } from "../../models/Inspection";
import { readingKey, type InspectionFormDraft } from "../utils/inspectionDraft";
import { REQUIRED_DIMENSION_MESSAGE } from "../../utils/inspectionFormValues";
import {
	angleMeasurementError,
	hasValidDimensionReading,
	measurementError,
	validateInspectionDate,
} from "../utils/inspectionValidation";
import {
	formatMeasurement,
	formatMeasurementInput,
	normalizeMeasurementInput,
} from "@/shared/utils/measurementPrecision";
import {
	InspectionDimensionTable,
	MeasurementErrorRow,
} from "./InspectionDimensionTable";
import { InspectionCheckToggle } from "./InspectionCheckToggle";
import { MeasurementTextField } from "./MeasurementTextField";
import { useMeasurementNavigation } from "../hooks/useMeasurementNavigation";
import { GAUGE_CODES, YES_NO_CODES } from "../constants/inspectionChecks";
import { hasNoDimensionTolerances } from "../utils/inspectionTolerances";

const INSPECTION_CONTAINER = "m1-inspection";
const GROUP_TWO_COLUMN_MIN_WIDTH = 540;
const GROUP_THREE_COLUMN_MIN_WIDTH = 720;
const RADII = ["A", "B", "C", "D"] as const;
const LINEAR_DIMENSIONS = ["1", "2", "3", "4"] as const;
const LEG_ANGLES = ["#1", "#2"] as const;
const SAMPLE_CHECKS = ["Bend Test", "Shot Blast", "MagnaFlux"] as const;

const measurementInputSx = {
	minWidth: 0,
	"& .MuiInputBase-root": {
		height: 26,
		fontSize: "0.75rem",
		fontFamily: "monospace",
	},
	"& .MuiInputAdornment-root": {
		ml: 0.25,
		fontSize: "inherit",
		"& p": { fontSize: "inherit" },
	},
	"& input": {
		px: 0.5,
		py: 0.25,
		textAlign: "center",
		fontVariantNumeric: "tabular-nums",
	},
	"& .MuiInputBase-root.Mui-error": {
		backgroundColor: (theme: Theme) =>
			alpha(theme.palette.error.main, theme.palette.action.selectedOpacity),
	},
};

interface FormSectionProps {
	title?: string;
	children: ReactNode;
	outlined?: boolean;
}

const stackedGrid = {
	display: "grid",
	minWidth: 0,
	gridTemplateColumns: "minmax(0, 1fr)",
	gap: 1,
} as const;

const fieldGrid = {
	...stackedGrid,
	[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_TWO_COLUMN_MIN_WIDTH}px)`]:
		{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
	[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_THREE_COLUMN_MIN_WIDTH}px)`]:
		{ gridTemplateColumns: "minmax(220px, 26fr) repeat(2, minmax(0, 37fr))" },
	alignItems: "stretch",
} as const;

const columnStack = {
	display: "flex",
	flexDirection: "column",
	minWidth: 0,
	gap: 1,
} as const;

const FormSection = ({
	title,
	children,
	outlined = false,
}: FormSectionProps) => (
	<Box
		component="section"
		sx={{
			minWidth: 0,
			...(outlined && { border: 1, borderColor: "divider", borderRadius: 1 }),
		}}
	>
		{title && (
			<Typography
				variant="body2"
				sx={{
					mb: 0.5,
					px: 0.5,
					minHeight: 20,
					fontSize: "0.75rem",
					fontWeight: 500,
					color: "text.secondary",
					backgroundColor: "action.hover",
				}}
			>
				{title}
			</Typography>
		)}
		{children}
	</Box>
);

type MeasurementFeedback = ReturnType<typeof measurementError>;
const noFeedback: MeasurementFeedback = { error: false };

// Each field and error row watches only its own value, so typing one
// measurement doesn't re-render the whole form.
const useScalarFeedback = (
	control: Control<InspectionFormDraft>,
	item: SectionSpecItem | undefined,
	disabled: boolean,
): MeasurementFeedback => {
	const raw = useWatch({
		control,
		name: `readings.${readingKey(item?.ProfileItemId ?? 0)}.NumericValue`,
	});
	return item && !disabled ? measurementError(raw ?? "", item) : noFeedback;
};

const useAngleFeedback = (
	control: Control<InspectionFormDraft>,
	item: SectionSpecItem | undefined,
	disabled: boolean,
): MeasurementFeedback => {
	const angle = useWatch({
		control,
		name: `angles.${readingKey(item?.ProfileItemId ?? 0)}`,
	});
	return item && !disabled
		? angleMeasurementError(angle?.degrees ?? "", angle?.minutes ?? "", item)
		: noFeedback;
};

interface MeasurementErrorProps {
	control: Control<InspectionFormDraft>;
	item?: SectionSpecItem;
	code: string;
	disabled: boolean;
	id: string;
}

const ScalarErrorRow = ({
	control,
	item,
	disabled,
	code,
	id,
}: MeasurementErrorProps) => {
	const { helperText } = useScalarFeedback(control, item, disabled);
	return helperText ? (
		<MeasurementErrorRow code={code} id={id} message={helperText} />
	) : null;
};

const AngleErrorRow = ({
	control,
	item,
	disabled,
	code,
	id,
}: MeasurementErrorProps) => {
	const { helperText } = useAngleFeedback(control, item, disabled);
	return helperText ? (
		<MeasurementErrorRow code={code} id={id} message={helperText} />
	) : null;
};

/** Registers the "at least one dimension" rule without subscribing the form to every reading. */
const ReadingsValidator = ({
	form,
	items,
}: {
	form: UseFormReturn<InspectionFormDraft>;
	items: SectionSpecItem[];
}) => {
	useController({
		name: "readings",
		control: form.control,
		rules: {
			validate: () =>
				hasValidDimensionReading(form.getValues(), items) ||
				REQUIRED_DIMENSION_MESSAGE,
		},
	});
	return null;
};

interface AngleFieldsProps {
	item?: SectionSpecItem;
	disabled: boolean;
	form: UseFormReturn<InspectionFormDraft>;
	errorId: string;
}

const AngleFields = ({ item, disabled, form, errorId }: AngleFieldsProps) => {
	const feedback = useAngleFeedback(form.control, item, disabled);
	return (
		<Box
			sx={{
				...stackedGrid,
				gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
				gap: 0.5,
			}}
		>
			{(["degrees", "minutes"] as const).map((part) => {
				const invalidPart =
					!feedback.invalidParts ||
					feedback.invalidParts.includes(
						part === "degrees" ? "Degrees" : "Minutes",
					);
				const input = (fieldProps: TextFieldProps) => (
					<MeasurementTextField
						{...fieldProps}
						sx={measurementInputSx}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										{part === "degrees" ? "°" : "′"}
									</InputAdornment>
								),
							},
							htmlInput: {
								inputMode: "numeric",
								"aria-label": part === "degrees" ? "Degrees" : "Minutes",
								"aria-describedby":
									invalidPart && feedback.helperText ? errorId : undefined,
							},
						}}
						type="text"
						size="small"
						fullWidth
						disabled={disabled || !item?.Applies}
						error={feedback.error && invalidPart}
					/>
				);
				if (!item?.Applies) return <Box key={part}>{input({ value: "" })}</Box>;
				const key = readingKey(item.ProfileItemId);
				return (
					<Controller
						key={`${key}.${part}`}
						name={`angles.${key}.${part}`}
						control={form.control}
						defaultValue=""
						rules={{
							deps: "readings",
							validate: () => {
								const angle = form.getValues("angles")[key];
								return (
									angleMeasurementError(
										angle?.degrees ?? "",
										angle?.minutes ?? "",
										item,
									).helperText ?? true
								);
							},
						}}
						render={({ field: { ref, ...field } }) =>
							input({ ...field, inputRef: ref, value: field.value ?? "" })
						}
					/>
				);
			})}
		</Box>
	);
};

export interface M1InspectionFormProps {
	assetId: number;
	form: UseFormReturn<InspectionFormDraft>;
	sectionId: number | null;
	items: SectionSpecItem[];
	editing: InspectionWithValues | null;
	spec: SectionSpec | null;
	weightProfileItemId: number | null;
	canSave: boolean;
	specError?: string;
	loading: boolean;
	hasSpec: boolean;
	specMissing: boolean;
	saving: boolean;
	onSubmit: () => void;
	onSectionChange?: (id: number | null) => void;
	onCancel: () => void;
	/** Sheet rows reuse the same controls and validation; entry columns remain the default. */
	layout?: "columns" | "sheet";
}

export const M1InspectionForm = ({
	assetId,
	form,
	sectionId,
	items,
	editing,
	spec,
	weightProfileItemId,
	canSave,
	specError,
	loading,
	hasSpec,
	specMissing,
	saving,
	onSubmit,
	onSectionChange,
	onCancel,
	layout = "columns",
}: M1InspectionFormProps) => {
	const isSheet = layout === "sheet";
	const { control } = form;
	const measurementId = useId();
	const errorId = (code: string) => `${measurementId}-${code}-error`;
	const { formHandlers } = useMeasurementNavigation();
	const busy = loading || saving;
	const hasPendingChanges = editing === null || form.formState.isDirty;
	const showNoTolerancesWarning =
		hasSpec && !loading && !specError && hasNoDimensionTolerances(items);
	const errorMessages = [
		form.formState.errors.root?.context?.message,
		form.formState.errors.readings?.message,
	].filter((message): message is string => typeof message === "string");
	const serverTimeZone = TimeService.getInstance().getServerTimeZone();
	const itemFor = (code: string) => items.find((item) => item.Code === code);
	const disabled = (code: string) =>
		busy || !hasSpec || !itemFor(code)?.Applies;
	const field = (code: string, label: string) => {
		const item = itemFor(code);
		const isDisabled = disabled(code);
		const input = (
			fieldProps: TextFieldProps,
			feedback: MeasurementFeedback = noFeedback,
		) => (
			<MeasurementTextField
				{...fieldProps}
				slotProps={{
					htmlInput: {
						"aria-label": `Entered ${label}`,
						"aria-describedby": feedback.helperText ? errorId(code) : undefined,
					},
				}}
				size="small"
				fullWidth
				disabled={isDisabled}
				error={feedback.error}
				sx={measurementInputSx}
			/>
		);
		if (!item?.Applies) return input({ value: "" });
		return (
			<Controller
				key={readingKey(item.ProfileItemId)}
				name={`readings.${readingKey(item.ProfileItemId)}.NumericValue`}
				control={control}
				defaultValue=""
				rules={{
					deps: "readings",
					validate: (raw) =>
						measurementError(raw ?? "", item).helperText ?? true,
				}}
				render={({ field: { ref, ...field } }) =>
					input(
						{
							...field,
							inputRef: ref,
							value: field.value ?? "",
							onChange: (event) =>
								field.onChange(
									normalizeMeasurementInput(event.target.value, code),
								),
							onBlur: () => {
								const formatted = formatMeasurementInput(
									field.value ?? "",
									code,
								);
								if (formatted !== field.value) field.onChange(formatted);
								field.onBlur();
							},
						},
						isDisabled ? noFeedback : measurementError(field.value ?? "", item),
					)
				}
			/>
		);
	};
	const checkToggle = (code: string, label: string) => {
		const item = itemFor(code);
		const input = (
			value: boolean | null,
			onChange: (value: boolean | null) => void,
			onBlur?: () => void,
		) => (
			<InspectionCheckToggle
				label={label}
				variant={YES_NO_CODES.has(code) ? "yes-no" : "go-no-go"}
				value={value}
				onBlur={onBlur}
				disabled={disabled(code)}
				onChange={onChange}
			/>
		);
		if (!item?.Applies) return input(null, () => {});
		return (
			<Controller
				key={readingKey(item.ProfileItemId)}
				name={`readings.${readingKey(item.ProfileItemId)}.Checked`}
				control={control}
				defaultValue={null}
				render={({ field }) =>
					input(field.value ?? null, field.onChange, field.onBlur)
				}
			/>
		);
	};
	const checkRows = (codes: readonly string[], labels: readonly string[]) =>
		codes.map((code, index) => ({
			code,
			label: labels[index],
			item: itemFor(code),
			entered: checkToggle(code, labels[index]),
		}));
	const measurementRow = (code: string, label: string) => {
		const item = itemFor(code);
		return {
			code,
			label,
			item,
			entered: field(code, label),
			error: (
				<ScalarErrorRow
					control={control}
					item={item}
					code={code}
					disabled={disabled(code)}
					id={errorId(code)}
				/>
			),
		};
	};
	const commentsField = (
		<Controller
			name="comments"
			control={control}
			render={({ field }) => (
				<TextField
					{...field}
					label="Comments"
					multiline
					minRows={3}
					fullWidth
					disabled={saving}
					size="small"
					sx={{
						minWidth: 0,
						...(isSheet
							? { gridColumn: "1 / -1", mt: 1, mb: 0 }
							: {
									[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_THREE_COLUMN_MIN_WIDTH}px)`]:
										{
											flex: 1,
											"& .MuiInputBase-root": {
												flex: 1,
												alignItems: "flex-start",
											},
											"& textarea:not([aria-hidden])": {
												height: "100% !important",
												boxSizing: "border-box",
											},
										},
								}),
					}}
				/>
			)}
		/>
	);
	return (
		<Paper
			component="form"
			{...formHandlers}
			onSubmit={onSubmit}
			noValidate
			aria-labelledby="entry-heading"
			elevation={2}
			sx={{
				p: 1.5,
				minWidth: 0,
				minHeight: "100%",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				// Scope measurement and check row density to this form.
				// 26px controls + cell borders fit a 28px border-box row.
				"&& table tr, && table th, && table td": {
					height: 28,
					boxSizing: "border-box",
					py: 0,
					px: 0.25,
					fontSize: "0.75rem",
					lineHeight: "24px",
				},
				// themeMESF defaults TextField to margin="dense" (8px top/4px bottom).
				// Reset table fields here; the metadata column separately normalizes its margins.
				"&& table .MuiTextField-root": { display: "flex", margin: 0 },
				"&& table .MuiToggleButtonGroup-root": {
					display: "flex",
					width: "max-content",
					mx: "auto",
				},
				"&& table .MuiInputBase-root": {
					height: 26,
					fontSize: "0.75rem",
					lineHeight: "24px",
				},
				"&& table input": {
					height: 24,
					boxSizing: "border-box",
					py: 0,
					px: 0.25,
					lineHeight: "24px",
				},
				"&& table .MuiToggleButton-root": {
					height: 26,
					minWidth: 24,
					py: 0,
					px: 0.25,
					fontSize: "0.75rem",
					lineHeight: "24px",
				},
			}}
		>
			<ReadingsValidator form={form} items={items} />
			<Stack
				spacing={1.25}
				useFlexGap
				sx={{
					minWidth: 0,
					flex: 1,
					containerType: "inline-size",
					containerName: INSPECTION_CONTAINER,
				}}
			>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
				>
					<Typography
						id="entry-heading"
						component="h2"
						variant="subtitle2"
						fontWeight={700}
					>
						{editing ? "Edit inspection" : "New Inspection"}
					</Typography>
				</Stack>
				{specError && <Alert severity="error">{specError}</Alert>}
				{specMissing && (
					<Alert severity="warning">
						No specification available for this section and sample date.
					</Alert>
				)}
				<Box
					sx={
						isSheet
							? {
									...stackedGrid,
									alignItems: "start",
									[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_TWO_COLUMN_MIN_WIDTH}px)`]:
										{
											gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
										},
								}
							: fieldGrid
					}
					data-testid="inspection-columns"
				>
					<Box
						sx={{
							...columnStack,
							gap: 2,
							...(isSheet && {
								...stackedGrid,
								gridColumn: "1 / -1",
								alignItems: "start",
								[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_TWO_COLUMN_MIN_WIDTH}px)`]:
									{
										gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
									},
								[`@container ${INSPECTION_CONTAINER} (min-width: ${GROUP_THREE_COLUMN_MIN_WIDTH}px)`]:
									{
										gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
									},
							}),
							// themeMESF's dense margins must not offset metadata from adjacent tables.
							"& .MuiTextField-root": { my: 0 },
						}}
					>
						{showNoTolerancesWarning && (
							<Alert
								severity="warning"
								variant="outlined"
								sx={{
									minWidth: 0,
									px: 1,
									py: 0.25,
									fontSize: "0.75rem",
									...(isSheet && { gridColumn: "1 / -1" }),
								}}
							>
								This section has no published tolerances yet. Readings are
								recorded without limit checks.
							</Alert>
						)}
						<Controller
							name="inspectedAt"
							control={control}
							rules={{ validate: validateInspectionDate }}
							render={({ field, fieldState }) => (
								<DateTimePicker
									label="Sample At"
									disableFuture
									value={field.value ? moment(field.value) : null}
									inputRef={field.ref}
									timezone={serverTimeZone}
									onChange={(value) =>
										field.onChange(value?.isValid() ? value.toDate() : null)
									}
									views={["year", "month", "day", "hours", "minutes"]}
									timeSteps={{ minutes: 1 }}
									disabled={saving}
									format="MM/DD/YYYY HH:mm"
									ampm={false}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
											onBlur: field.onBlur,
											error: !!fieldState.error,
											helperText: fieldState.error?.message,
											sx: { minWidth: 0 },
										},
									}}
								/>
							)}
						/>
						<Controller
							name="sectionId"
							control={control}
							rules={{ required: "Section is required" }}
							render={({ field, fieldState }) => (
								<SectionSelector
									value={field.value}
									onChange={(id) => {
										// The heat list is per section: a kept heat would be saved
										// while the field shows empty.
										if (editing && id !== field.value)
											form.setValue("heatId", null, { shouldDirty: true });
										field.onChange(id);
										onSectionChange?.(id);
									}}
									fieldError={fieldState.error}
									assetId={assetId}
									sortBy="recent"
									required
									size="small"
									hideTph
									disabled={saving}
								/>
							)}
						/>
						<Box sx={{ "& .MuiAutocomplete-root": { width: "100%" } }}>
							<InspectionHeatField
								control={control}
								assetId={assetId}
								sectionId={sectionId}
							/>
						</Box>
						<Controller
							name="measuredLbsPerFoot"
							control={control}
							rules={{
								validate: (raw) => {
									const result = measurementError(raw, {
										Ltl: spec?.MinLbsPerFoot ?? null,
										Utl: spec?.MaxLbsPerFoot ?? null,
									});
									return result.helperText ?? true;
								},
							}}
							render={({ field, fieldState }) => {
								const feedback = measurementError(field.value, {
									Ltl: spec?.MinLbsPerFoot ?? null,
									Utl: spec?.MaxLbsPerFoot ?? null,
								});
								return (
									<MeasurementTextField
										{...field}
										inputRef={field.ref}
										label="Weight lb/ft"
										size="small"
										fullWidth
										disabled={busy || !hasSpec || weightProfileItemId === null}
										error={!!fieldState.error || feedback.error}
										helperText={[
											spec?.TargetLbsPerFoot == null
												? "no target loaded"
												: `target ${formatMeasurement(spec.TargetLbsPerFoot)}`,
											fieldState.error?.message ?? feedback.helperText,
										]
											.filter(Boolean)
											.join(" — ")}
										onBlur={() => {
											field.onChange(
												formatMeasurementInput(field.value, "WEIGHT"),
											);
											field.onBlur();
										}}
									/>
								);
							}}
						/>
						{!isSheet && commentsField}
					</Box>
					<Box sx={{ ...columnStack, gap: 0 }}>
						<FormSection outlined>
							<InspectionDimensionTable
								label="Radii, leg angles, stencil and samples"
								groups={[
									{
										label: "Rad's",
										rows: [
											...RADII.map((radius) =>
												measurementRow(`R${radius}`, radius),
											),
											...checkRows(["RALL"], ["Other Rad' OK"]).map((row) => ({
												...row,
												check: true,
											})),
										],
									},
									{
										label: "Leg Angles",
										rows: LEG_ANGLES.map((label, index) => {
											const code = `LA${index + 1}`;
											const item = itemFor(code);
											return {
												code,
												label,
												angle: true,
												item,
												error: (
													<AngleErrorRow
														control={control}
														item={item}
														code={code}
														disabled={disabled(code)}
														id={errorId(code)}
													/>
												),
												entered: (
													<AngleFields
														item={item}
														disabled={disabled(code)}
														form={form}
														errorId={errorId(code)}
													/>
												),
											};
										}),
									},
									{
										label: "Stencil",
										rows: [
											measurementRow("STHGT", "Height"),
											...checkRows(["STLEG"], ["Legible"]).map((row) => ({
												...row,
												check: true,
											})),
										],
									},
									{
										label: "Samples",
										rows: checkRows(
											["BEND", "SHOT", "MAGNA"],
											SAMPLE_CHECKS,
										).map((row) => ({ ...row, check: true })),
									},
								]}
							/>
						</FormSection>
					</Box>
					<Box sx={{ ...stackedGrid, alignContent: "start" }}>
						<FormSection outlined>
							<InspectionDimensionTable
								label="Linear dimensions and gauges"
								groups={[
									{
										label: "Linear Dim's",
										rows: LINEAR_DIMENSIONS.map((code) =>
											measurementRow(code, code),
										),
									},
									{
										label: "Go / No Go Gauges",
										rows: Array.from(GAUGE_CODES, (code) => ({
											code,
											label: code,
											item: itemFor(code),
											entered: checkToggle(code, `Entered gauge ${code}`),
										})),
									},
								]}
							/>
						</FormSection>
					</Box>
					{isSheet && commentsField}
				</Box>

				<Stack
					direction="row"
					alignItems="flex-end"
					justifyContent="flex-end"
					sx={{ minWidth: 0, flexWrap: "wrap", gap: 1, mt: "auto" }}
				>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							// Reserve one compact alert row without increasing the footer height.
							minHeight: 32,
							flexBasis: { xs: "100%", sm: 0 },
						}}
					>
						{errorMessages.length > 0 && (
							<Alert
								severity="error"
								sx={{
									py: 0,
									minHeight: 32,
									"& .MuiAlert-icon, & .MuiAlert-message": { py: 0.5 },
								}}
							>
								{errorMessages.map((message) => (
									<Typography key={message} variant="body2">
										{message}
									</Typography>
								))}
							</Alert>
						)}
					</Box>
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
					>
						{editing && (
							<Button
								type="button"
								size="small"
								disabled={busy}
								onClick={onCancel}
							>
								Cancel edit
							</Button>
						)}
						<Button
							disabled={saving || !canSave || !hasPendingChanges}
							type="submit"
							variant="contained"
							size="small"
							sx={{ maxWidth: "100%" }}
						>
							{editing ? "Update" : "Save inspection"}
						</Button>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
};
